import { getContactImage } from '@app/utils/profileImage'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as React from 'react'
import { Channel as StreamChannel } from 'stream-chat'
import {
  Channel,
  ChannelList,
  Chat as ChatComponent,
  EmptyStateIndicator,
  InfiniteScroll,
  MessageInput,
  MessageList,
  MessageSimple,
  Thread,
  useChatContext,
  useCreateChatClient,
  Window,
  WithComponents
} from 'stream-chat-react'
import { handleFileUpload, syncUser } from './actions'
import ChannelHeader from './ChannelHeader'
import ChannelPreviewActionButtons from './ChannelPreviewActionButtons'
import EditChannelModal from './EditChannelModal'

import 'stream-chat-react/dist/css/v2/index.css'
import './Chat.scss'

type IComponentProps = {
  apiKey: string
  chatToken: string
  userData: {
    id: string
    name: string
  }
  onError: (err: Error) => void
}

const Chat = (props: IComponentProps) => {
  const client = useCreateChatClient({
    apiKey: props.apiKey,
    tokenOrProvider: props.chatToken,
    userData: {
      ...props.userData,
      image: getContactImage(props.userData.id, 50),
    },
  })

  React.useEffect(() => {
    if (!client) return

    // Updates permissions and channels for the user based on their
    // HB identity, but must be called after the chat client is connected.
    syncUser()
  }, [client])

  if (!client) {
    return null
  }

  return (
    <div className='chat-container'>
      <ChatComponent client={client} theme='str-chat__theme-custom'>
        <ChatBody
          userData={props.userData}
          onError={props.onError}
        />
      </ChatComponent>
    </div>
  )
}
export default Chat

type IChatBodyProps = {
  userData: {
    id: string
    name: string
  }
  onError: (err: Error) => void
}

type EditModal = {
  isOpen: boolean
  channel: StreamChannel | null
  action?: 'addMembers'
}

const ChatBody = (props: IChatBodyProps) => {
  const [mobileChannelListOpen, setMobileChannelListOpen] = React.useState<boolean>(false)
  const [editModal, setEditModal] = React.useState<EditModal>({ isOpen: false, channel: null })

  const { client } = useChatContext()

  const firstActiveChannel = React.useMemo(() => {
    const params = new URLSearchParams(location.search)
    return params.get('channel')
  }, [])

  React.useEffect(() => {
    if (!firstActiveChannel) {
      return
    }

    const check = async () => {
      const [channel] = await client.queryChannels({
        cid: `messaging:${firstActiveChannel}`, // Filtering by cid is faster
        members: { $in: [props.userData.id] },
      })
  
      // Check if the ?channel= query param is valid: a channel
      // exists with the given ID, and the user is a member of it.
      if (!channel) {
        props.onError(new Error('Channel not found or you do not have permission to view it'))
        setMobileChannelListOpen(true) // Make sure user can see the channel list
      }
    }
    check()
  }, [client, firstActiveChannel, props])

  return (
    <>
      <div
        className={`chat-channel-list-container ${
          mobileChannelListOpen
            ? 'chat-channel-list-container-mobile-open'
            : ''
        }`}
      >
        <WithComponents overrides={{ ChannelPreviewActionButtons }}>
          <ChannelList
            customActiveChannel={firstActiveChannel || undefined}
            showChannelSearch
            additionalChannelSearchProps={{
              searchForChannels: true,
              searchQueryParams: {
                userFilters: {
                  filters: {
                    id: '--non-existing-id--', // Prevent users from appearing in search
                  }
                }
              }
            }}
            filters={{
              type: 'messaging',
              members: { $in: [props.userData.id] },
            }}
            sort={{ last_message_at: -1 }}
            options={{ limit: 30 }}
            Paginator={InfiniteScroll}
            EmptyStateIndicator={() => (
              // Must be defined, otherwise add channel button won't show
              <ChatChannelList
                onAddChannel={() => setEditModal({ isOpen: true, channel: null })}
              >
                <EmptyStateIndicator listType='channel' />
              </ChatChannelList>
            )}
            renderChannels={(channels, preview) => (
              <ChatChannelList
                onAddChannel={() =>
                  setEditModal({ isOpen: true, channel: null })
                }
              >
                {
                  channels
                    // Sort archived channels to the bottom
                    .sort((a, b) => a.data?.readOnly ? 1 : b.data?.readOnly ? -1 : 0)
                    .map(c => (
                      <a key={c.id} onClick={() => setMobileChannelListOpen(false)}>
                        {preview(c)}
                      </a>
                    ))
                }
              </ChatChannelList>
            )}
          />
        </WithComponents>
      </div>
      <ChatChannel
        onError={props.onError}
        setMobileChannelListOpen={setMobileChannelListOpen}
        setEditModal={setEditModal}
      />

      {editModal.isOpen && (
        <EditChannelModal
          isOpen
          client={client}
          channel={editModal.channel}
          action={editModal.action}
          onClose={() => setEditModal({ isOpen: false, channel: null })}
          onError={(err) => {
            setEditModal({ isOpen: false, channel: null })
            props.onError(err)
          }}
        />
      )}
    </>
  )
}

// This component is not compatible with stream-chat-react ChannelList as
// it only implements the list of channels, not the search functionality.
const ChatChannelList = ({
  children,
  onAddChannel
}: React.PropsWithChildren<{
  onAddChannel: () => void
}>) => {
  return (
    <div className='chat-channel-list'>
      <div className='chat-channel-list-header'>
        <h3>Channels</h3>
        <a className='chat-channel-list-header-button-add' onClick={onAddChannel}>
          <FontAwesomeIcon icon={['fal', 'plus']} size={'lg'} />
        </a>
      </div>
      {children}
    </div>
  )
}

const ChatChannel = (props: {
  onError: (err: Error) => void
  setMobileChannelListOpen: (open: boolean) => void
  setEditModal: (modal: EditModal) => void
}) => {
  const { client, channel } = useChatContext()

  const uploadRequest = React.useCallback(async (file) => {
    if (channel?.data?.readOnly) {
      const error = new Error('Cannot upload files in an archived channel')
      props.onError(error)
      throw error
    }
    return handleFileUpload(file!)
  }, [channel?.data?.readOnly, props])

  return (
    <Channel
      markReadOnMount={false}
      doMarkReadRequest={async (channel, setChannelUnreadUiState) => {
        // Mark the channel as read after a 5 second delay
        await new Promise<void>((resolve) => setTimeout(resolve, 5000));
        const response = await channel.markRead();
        if (!response) {
          const error = new Error('Failed to mark channel as read')
          props.onError(error)
          throw error
        }
        if (setChannelUnreadUiState) {
          setChannelUnreadUiState({ last_read: new Date(), unread_messages: 0 });
        }
        return response;
      }}
      Message={(props: Parameters<typeof MessageSimple>[0]) => {
        const { isMyMessage, ...rest } = props
        // Left-align messages from current user
        return <div data-mine={false}>
          <MessageSimple {...rest} isMyMessage={() => false} />
        </div>
      }}
      doDeleteMessageRequest={async (message) => {
        if (channel?.data?.readOnly) {
          const error = new Error('Cannot delete messages in an archived channel')
          props.onError(error)
          throw error
        }
        return (await client.deleteMessage(message.id)).message
      }}
      doSendMessageRequest={async (channel, message) => {
        if (channel?.data?.readOnly) {
          const error = new Error('Cannot send messages in an archived channel')
          props.onError(error)
          throw error
        }
        return channel.sendMessage(message)
      }}
      doUpdateMessageRequest={async (_, updatedMessage, options) => {
        if (channel?.data?.readOnly) {
          const error = new Error('Cannot edit messages in an archived channel')
          props.onError(error)
          throw error
        }
        return client.updateMessage(updatedMessage, client.userID, options)
      }}
    >
      <Window>
        <ChannelHeader
          onShowMobileChannelList={() => props.setMobileChannelListOpen(true)}
          onShowChannelEditModal={(channel, action) => props.setEditModal({ isOpen: true, channel, action })}
        />
        <MessageList />
        <MessageInput
          grow
          doFileUploadRequest={uploadRequest}
          doImageUploadRequest={uploadRequest}
        />
      </Window>
      <Thread />
    </Channel>
  )
}
